import {
  FormControl,
  FormGroup,
  Grid,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { useContext, useState } from 'react';

import Stack from '@/contents//General/Stack';
import { Checkbox, FormControlLabel } from '@/contents/CampSite/styles';
import { ConstSearch } from '@/contents/constants';
import Button from '@/contents/General/Button';
import Icon from '@/contents/General/Icon';
import { Board, EditFooter } from '@/contents/General/Panels';
import Typography from '@/contents/General/Typography';
import { filterCheckedList } from '@/contents/hooks/searchHooks';
import { GlobalContext } from '@/contents/hooks/state';
import theme from '@/styles/theme';
import { valueToName } from '@/utils/ConstSearchUtil';

import SelectArea from './SelectArea';
import SelectChoice from './SelectChoice';

const Container = styled(Box)`
  padding-bottom: ${theme.spacing(7)};
  width: 100%;
  height: 100%;
  max-width: inherit;
  max-height: inherit;
  background: #f4f8f6;
  z-index: 12;
  overflow-y: auto;
`;

const SearchCondition = () => {
  const {
    dialogStatus,
    setDialogStatus,
    checkedList,
    setCheckedList,
    searchTotal,
  } = useContext(GlobalContext);
  const [showSelectArea, setShowSelectArea] = useState(false);
  const [showSelectChoice, setShowSelectChoice] = useState(false);
  const isPC = useMediaQuery('(min-width:900px)');

  const changeCheckedList = (value: string) => {
    if (checkedList.find((e) => e === value)) {
      setCheckedList(checkedList.filter((e) => e !== value));
    } else {
      setCheckedList([...checkedList, value]);
    }
  };

  const isChecked = (value: string) => {
    return checkedList.some((checked) => checked === value);
  };

  const close = () => {
    setDialogStatus({ ...dialogStatus, open: false });
  };

  const clear = () => {
    setCheckedList([]);
  };

  return (
    <>
      <Container>
        <Box paddingTop={5}>
          <Box
            sx={{
              padding: `0 ${theme.spacing(3)}`,
              paddingBottom: theme.spacing(2),
            }}
          >
            <Typography fontSize={16} fontWeight={700}>
              エリア
            </Typography>
          </Box>
          <Box padding={`0 ${theme.spacing(3)}`}>
            <Button
              component="label"
              onClick={() => setShowSelectArea(true)}
              fullWidth
              sx={{
                padding: 2,
                border: 'solid 1px #e3e9e6',
                borderRadius: '8px',
                background: '#ffffff',
              }}
            >
              <Stack alignCenter between sx={{ width: '100%' }}>
                {filterCheckedList('area', checkedList)?.length === 0 ? (
                  <Typography fontSize={16} fontWeight={500} color="#C2C8C4">
                    地方区分や都道府県を選択
                  </Typography>
                ) : (
                  <Stack flexWrap="wrap">
                    {filterCheckedList('area', checkedList)?.map((item, i) => {
                      return (
                        <Box key={item} paddingRight={1}>
                          <Typography fontSize={14}>
                            {valueToName(item)}{' '}
                            {i < checkedList.length - 1 && ','}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
                <IconButton>
                  <Icon iconType="add" />
                </IconButton>
              </Stack>
            </Button>
          </Box>
        </Box>
        <Box paddingTop={5}>
          <Box
            sx={{
              padding: `0 ${theme.spacing(3)}`,
              paddingBottom: theme.spacing(2),
            }}
          >
            <Typography fontSize={16} fontWeight={700}>
              ロケーション
            </Typography>
          </Box>
          <Box padding={`0 ${theme.spacing(3)}`}>
            <Board>
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormGroup>
                  <Grid container spacing={2}>
                    {ConstSearch.location.map((item, index) => (
                      <Grid item xs={6} key={`${item.label + index}`}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              value={item.value}
                              onChange={(e) =>
                                changeCheckedList(e.target.value)
                              }
                              checked={isChecked(item.value)}
                              icon={<Icon iconType="checkbox-off" />}
                              checkedIcon={<Icon iconType="checkbox-on" />}
                            />
                          }
                          label={
                            <Typography
                              fontSize={14}
                              fontWeight={500}
                              sx={{ lineHeight: 1.3 }}
                            >
                              {item.label}
                            </Typography>
                          }
                          className={isChecked(item.value) ? 'checked' : ''}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </FormControl>
            </Board>
          </Box>
        </Box>
        <Box paddingTop={3}>
          <Box
            sx={{
              padding: `0 ${theme.spacing(3)}`,
              paddingBottom: theme.spacing(2),
            }}
          >
            <Typography fontSize={16} fontWeight={700}>
              施設タイプ
            </Typography>
          </Box>
          <Box padding={`0 ${theme.spacing(3)}`}>
            <Board>
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormGroup>
                  <Grid container spacing={2}>
                    {ConstSearch.equipment
                      .filter((item) => {
                        // 以下3つは検索条件には含めない
                        return ![
                          'facilityTrailerhouse',
                          'facilityTipi',
                          'facilityTreehouse',
                        ].includes(item.value);
                      })
                      .map((item, index) => (
                        <Grid item xs={6} key={`${item.label + index}`}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                value={item.value}
                                onChange={(e) => {
                                  changeCheckedList(e.target.value);
                                }}
                                checked={isChecked(item.value)}
                                icon={<Icon iconType="checkbox-off" />}
                                checkedIcon={<Icon iconType="checkbox-on" />}
                              />
                            }
                            label={
                              <Typography
                                fontSize={14}
                                fontWeight={500}
                                sx={{ lineHeight: 1.3 }}
                              >
                                {item.label}
                              </Typography>
                            }
                            className={isChecked(item.value) ? 'checked' : ''}
                          />
                        </Grid>
                      ))}
                  </Grid>
                </FormGroup>
              </FormControl>
            </Board>
          </Box>
        </Box>
        <Box paddingTop={3}>
          <Box
            sx={{
              padding: `0 ${theme.spacing(3)}`,
              paddingBottom: theme.spacing(2),
            }}
          >
            <Typography fontSize={16} fontWeight={700}>
              車両乗り入れ
            </Typography>
          </Box>
          <Box padding={`0 ${theme.spacing(3)}`}>
            <Board>
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormGroup>
                  <Grid container spacing={2}>
                    {ConstSearch.carStock.map((item, index) => (
                      <Grid item xs={6} key={`${item.label + index}`}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              value={item.value}
                              onChange={(e) =>
                                changeCheckedList(e.target.value)
                              }
                              checked={isChecked(item.value)}
                              icon={<Icon iconType="checkbox-off" />}
                              checkedIcon={<Icon iconType="checkbox-on" />}
                            />
                          }
                          label={
                            <Typography
                              fontSize={14}
                              fontWeight={500}
                              sx={{ lineHeight: 1.3 }}
                            >
                              {item.label}
                            </Typography>
                          }
                          className={isChecked(item.value) ? 'checked' : ''}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </FormControl>
            </Board>
          </Box>
        </Box>
        <Box paddingTop={3}>
          <Box
            sx={{
              padding: `0 ${theme.spacing(3)}`,
              paddingBottom: theme.spacing(2),
            }}
          >
            <Typography fontSize={16} fontWeight={700}>
              お風呂・温泉施設
            </Typography>
          </Box>
          <Box padding={`0 ${theme.spacing(3)}`}>
            <Board>
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormGroup>
                  <Grid container spacing={2}>
                    {ConstSearch.bath.map((item, index) => (
                      <Grid item xs={6} key={`${item.label + index}`}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              value={item.value}
                              onChange={(e) =>
                                changeCheckedList(e.target.value)
                              }
                              checked={isChecked(item.value)}
                              icon={<Icon iconType="checkbox-off" />}
                              checkedIcon={<Icon iconType="checkbox-on" />}
                            />
                          }
                          label={
                            <Typography
                              fontSize={14}
                              fontWeight={500}
                              sx={{ lineHeight: 1.3 }}
                            >
                              {item.label}
                            </Typography>
                          }
                          className={isChecked(item.value) ? 'checked' : ''}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </FormControl>
            </Board>
          </Box>
          <Box paddingTop={3}>
            <Box
              sx={{
                padding: `0 ${theme.spacing(3)}`,
                paddingBottom: theme.spacing(2),
              }}
            >
              <Typography fontSize={16} fontWeight={700}>
                営業期間
              </Typography>
            </Box>
            <Box padding={`0 ${theme.spacing(3)}`}>
              <Board>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <FormGroup>
                    <Grid container spacing={2}>
                      {ConstSearch.businessPeriod.map((item, index) => (
                        <Grid item xs={6} key={`${item.label + index}`}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                value={item.value}
                                onChange={(e) =>
                                  changeCheckedList(e.target.value)
                                }
                                checked={isChecked(item.value)}
                                icon={<Icon iconType="checkbox-off" />}
                                checkedIcon={<Icon iconType="checkbox-on" />}
                              />
                            }
                            label={
                              <Typography
                                fontSize={14}
                                fontWeight={500}
                                sx={{ lineHeight: 1.3 }}
                              >
                                {item.label}
                              </Typography>
                            }
                            className={isChecked(item.value) ? 'checked' : ''}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </FormGroup>
                </FormControl>
              </Board>
            </Box>
          </Box>
          <Box paddingTop={3}>
            <Box
              sx={{
                padding: `0 ${theme.spacing(3)}`,
                paddingBottom: theme.spacing(2),
              }}
            >
              <Stack alignEnd space={1}>
                <Typography fontSize={16} fontWeight={700}>
                  チェックイン・アウト
                </Typography>
                <Typography fontSize={12} fontWeight={500} color="gray">
                  ※常時サービスとして
                </Typography>
              </Stack>
            </Box>
            <Box padding={`0 ${theme.spacing(3)}`}>
              <Board>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <FormGroup>
                    <Grid container spacing={2}>
                      {ConstSearch.checkIn.map((item, index) => (
                        <Grid item xs={6} key={`${item.value + index}`}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                value={item.value}
                                onChange={(e) =>
                                  changeCheckedList(e.target.value)
                                }
                                checked={isChecked(item.value)}
                                icon={<Icon iconType="checkbox-off" />}
                                checkedIcon={<Icon iconType="checkbox-on" />}
                              />
                            }
                            label={
                              <Typography
                                fontSize={14}
                                fontWeight={500}
                                sx={{ lineHeight: 1.3 }}
                              >
                                {item.label}
                              </Typography>
                            }
                            className={isChecked(item.value) ? 'checked' : ''}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </FormGroup>
                </FormControl>
              </Board>
            </Box>
          </Box>
          <Box paddingTop={5}>
            <Box
              sx={{
                padding: `0 ${theme.spacing(3)}`,
                paddingBottom: theme.spacing(2),
              }}
            >
              <Typography fontSize={16} fontWeight={700}>
                こだわり条件
              </Typography>
            </Box>
            <Box padding={`0 ${theme.spacing(3)}`}>
              <Button
                component="label"
                onClick={() => setShowSelectChoice(true)}
                fullWidth
                color="white"
                sx={{
                  padding: 2,
                  border: 'solid 1px #e3e9e6',
                  borderRadius: '8px',
                  background: '#ffffff',
                }}
              >
                <Stack alignCenter between sx={{ width: '100%' }}>
                  {filterCheckedList('detail', checkedList)?.length === 0 ? (
                    <Typography fontSize={16} fontWeight={500} color="#C2C8C4">
                      サービスやアクティビティを選択
                    </Typography>
                  ) : (
                    <Stack flexWrap="wrap">
                      {filterCheckedList('detail', checkedList)?.map(
                        (item, i) => {
                          return (
                            <Box key={item} paddingRight={1}>
                              <Typography fontSize={14}>
                                {valueToName(item)}{' '}
                                {i < checkedList.length - 1 && ','}
                              </Typography>
                            </Box>
                          );
                        }
                      )}
                    </Stack>
                  )}
                  <IconButton>
                    <Icon iconType="add" />
                  </IconButton>
                </Stack>
              </Button>
            </Box>
          </Box>
        </Box>
        <EditFooter>
          <Stack
            space={1}
            between
            sx={{ p: `0 ${theme.spacing(3)}`, width: '100%' }}
          >
            <Stack alignEnd>
              {searchTotal !== null ? (
                <>
                  <Typography fontSize={28} fontWeight={900} letterSpacing={0}>
                    {searchTotal <= 100 ? searchTotal : '100'}
                  </Typography>
                  <Typography
                    fontSize={12}
                    whiteSpace="nowrap"
                    sx={{ paddingLeft: '4px', paddingBottom: '8px' }}
                  >
                    件{searchTotal > 100 && '以上'}
                  </Typography>
                </>
              ) : (
                <></>
              )}
            </Stack>
            <Stack space={2}>
              <Button
                onClick={clear}
                buttonSize="md"
                color="white"
                sx={{ padding: '5px 10px', minWidth: isPC ? '140px' : '100px' }}
              >
                条件をクリア
              </Button>
              <Button
                onClick={close}
                buttonSize="md"
                color="primary"
                sx={{ padding: '5px 10px', minWidth: isPC ? '140px' : '100px' }}
              >
                検索
              </Button>
            </Stack>
          </Stack>
        </EditFooter>
      </Container>
      {showSelectArea && <SelectArea setShowSelectArea={setShowSelectArea} />}
      {showSelectChoice && (
        <SelectChoice setShowSelectChoice={setShowSelectChoice} />
      )}
    </>
  );
};

export default SearchCondition;
